import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Drawer, Spin,
} from 'antd';
import DrawerExistingFollowUpEditor from '../CallFollowUps/DrawerExistingFollowUpEditor';
import {
  fetchCallFollowUp,
  fetchCallFollowUpQueueOptions,
} from '../CallFollowUps/callFollowUpApi';
import {
  VoiceCallContextTitle,
  VoiceCallMetaGrid,
  VoiceCallRecording,
  VoiceCallSummary,
  VoiceCallTranscript,
} from '../VoiceCallContext';

const safeDetailError = (error) => {
  if (error?.response?.status === 403) return 'You do not have access to this follow-up.';
  if (error?.response?.status === 404) return 'Follow-up was not found or is no longer accessible.';

  return 'Unable to load follow-up details.';
};

const FollowUpDetailDrawer = ({
  followUpId,
  onClose,
  onFollowUpChanged,
  onNavigatePrevious,
  onNavigateNext,
  canNavigatePrevious,
  canNavigateNext,
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState(null);
  const open = !!followUpId;
  const callLog = detail?.database_call_log || {};
  const transcript = Array.isArray(callLog.transcript) ? JSON.stringify(callLog.transcript) : callLog.transcript;

  useEffect(() => {
    if (!followUpId) {
      setDetail(null);
      setError(null);
      setOptions(null);
      setOptionsError(null);
      setOptionsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setDetail(null);
    setLoading(true);
    setError(null);
    setOptions(null);
    setOptionsError(null);

    fetchCallFollowUp(followUpId)
      .then((result) => {
        if (!cancelled) setDetail(result);
      })
      .catch((detailError) => {
        if (!cancelled) setError(safeDetailError(detailError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [followUpId]);

  useEffect(() => {
    const databaseCallLogId = detail?.database_call_log?.id;
    if (!databaseCallLogId) {
      setOptions(null);
      setOptionsError(null);
      setOptionsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setOptionsLoading(true);
    setOptionsError(null);
    fetchCallFollowUpQueueOptions({
      database_call_log_id: databaseCallLogId,
      selected_assignee_id: detail.assignee_id,
    })
      .then((result) => {
        if (!cancelled) setOptions(result || {});
      })
      .catch((optionsLoadError) => {
        if (!cancelled) {
          setOptionsError(
            optionsLoadError?.response?.status === 404
              ? 'Unable to load drawer options. Close and reopen the follow-up to refresh.'
              : 'Unable to load drawer options. Editing is unavailable until options load.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [detail?.id, detail?.database_call_log?.id, detail?.assignee_id]);

  const handleEditorChanged = (updatedFollowup) => {
    setDetail(updatedFollowup);
    onFollowUpChanged(updatedFollowup);
  };

  return (
    <Drawer
      visible={open}
      onClose={onClose}
      width={window.innerWidth > 768 ? 900 : '100%'}
      closable={false}
      className="follow-up-detail-drawer"
      title={(
        <VoiceCallContextTitle
          callLog={callLog}
          variant="follow-up"
          timestampField="started_at"
          includeDuration={false}
          showMetaCards={false}
          onNavigatePrevious={onNavigatePrevious}
          onNavigateNext={onNavigateNext}
          onClose={onClose}
          canNavigatePrevious={canNavigatePrevious}
          canNavigateNext={canNavigateNext}
          previousLabel="Previous follow-up"
          nextLabel="Next follow-up"
          closeLabel="Close follow-up details"
        />
      )}
    >
      <div className="follow-up-detail-body">
        {loading && <Spin size="small" />}
        {error && <Alert message={error} type="error" showIcon />}
        {!loading && !error && detail && (
          <>
            {optionsError && <Alert message={optionsError} type="warning" showIcon />}
            <VoiceCallMetaGrid callLog={callLog} variant="follow-up" timestampField="started_at" includeDuration={false} />
            <VoiceCallSummary callLog={callLog} variant="follow-up" />
            <DrawerExistingFollowUpEditor
              key={followUpId}
              followup={detail}
              options={options}
              optionsLoading={optionsLoading}
              optionsError={optionsError}
              onFollowUpChanged={handleEditorChanged}
            />
            <VoiceCallTranscript callLog={callLog} transcript={transcript} defaultExpanded={false} variant="follow-up" />
            <VoiceCallRecording
              callLog={callLog}
              callId={callLog.firestore_id}
              isOutbound={false}
              variant="follow-up"
              recordingAvailable={!!(callLog.recording_available && callLog.firestore_id)}
              showFeedback={!!callLog.call_sid}
            />
          </>
        )}
      </div>
    </Drawer>
  );
};

FollowUpDetailDrawer.propTypes = {
  followUpId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func,
  onFollowUpChanged: PropTypes.func,
  onNavigatePrevious: PropTypes.func,
  onNavigateNext: PropTypes.func,
  canNavigatePrevious: PropTypes.bool,
  canNavigateNext: PropTypes.bool,
};

FollowUpDetailDrawer.defaultProps = {
  followUpId: null,
  onClose: () => {},
  onFollowUpChanged: () => {},
  onNavigatePrevious: () => {},
  onNavigateNext: () => {},
  canNavigatePrevious: false,
  canNavigateNext: false,
};

export default FollowUpDetailDrawer;
